import React, { useEffect, useMemo, useState } from 'react';
/**
 * RatioTableRenderer — Fill hidden cells in a ratio table.
 *
 * DDD co-locatie: moved from components/practice/renderers/ in s67.
 */
import { QuestionRendererProps } from './types';
import { LatexRenderer } from '../../../components/ui/LatexRenderer';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
interface EditableRatioCell {
  key: string;
  row: number;
  col: number;
}
export function RatioTableRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const config = question.data || {};
  const headers: string[] = config.headers || [];
  const rows: unknown[][] = config.rows || [];
  const hiddenCells: Array<{
    row: number;
    col: number;
  }> = config.hiddenCells || [];
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const getKey = (row: number, col: number) => `${row}-${col}`;
  const editableCells = useMemo<EditableRatioCell[]>(() => hiddenCells.filter(cell => rows[cell.row]?.[cell.col] !== undefined).map(cell => ({
    key: getKey(cell.row, cell.col),
    row: cell.row,
    col: cell.col
  })), [hiddenCells, rows]);
  useEffect(() => {
    setInputs({});
  }, [question.id]);
  useEffect(() => {
    const allFilled = editableCells.every(cell => {
      const value = inputs[cell.key];
      return value && value.trim().length > 0;
    });
    onReadyChange?.(allFilled && editableCells.length > 0);
  }, [inputs, editableCells, onReadyChange]);
  const answerPayload = useMemo(() => JSON.stringify(inputs), [inputs]);
  useEdgeTriggeredSubmit(answerPayload, shouldValidate, onAnswer, question.id);
  const isHidden = (row: number, col: number) => editableCells.some(cell => cell.row === row && cell.col === col);
  const focusCell = (key: string) => {
    document.getElementById(`ratio-table-${question.id}-${key}`)?.focus();
  };
  const findDirectionalCell = (current: EditableRatioCell, direction: 'left' | 'right' | 'up' | 'down') => {
    const candidates = editableCells.filter(cell => {
      if (direction === 'left') {
        return cell.row === current.row && cell.col < current.col;
      }
      if (direction === 'right') {
        return cell.row === current.row && cell.col > current.col;
      }
      if (direction === 'up') {
        return cell.col === current.col && cell.row < current.row;
      }
      return cell.col === current.col && cell.row > current.row;
    });
    return candidates.sort((a, b) => {
      const aDistance = direction === 'left' || direction === 'right' ? Math.abs(a.col - current.col) : Math.abs(a.row - current.row);
      const bDistance = direction === 'left' || direction === 'right' ? Math.abs(b.col - current.col) : Math.abs(b.row - current.row);
      return aDistance - bDistance;
    })[0];
  };
  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, cell: EditableRatioCell) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const index = editableCells.findIndex(candidate => candidate.key === cell.key);
      const nextCell = editableCells[index + 1];
      if (nextCell) focusCell(nextCell.key);else event.currentTarget.blur();
      return;
    }
    if (!event.altKey) return;
    const directions: Record<string, 'left' | 'right' | 'up' | 'down'> = {
      ArrowLeft: 'left',
      ArrowRight: 'right',
      ArrowUp: 'up',
      ArrowDown: 'down'
    };
    const direction = directions[event.key];
    if (!direction) return;
    const nextCell = findDirectionalCell(cell, direction);
    if (nextCell) {
      event.preventDefault();
      focusCell(nextCell.key);
    }
  };
  const cellToString = (value: unknown): string => value === null || value === undefined ? '' : String(value);
  const tableId = `ratio-table-${question.id}`;
  return <div className="w-full max-w-3xl mx-auto" data-id="element-12433" data-mp="20plui3ls9tuc">
      <div className="bg-white rounded-xl border-2 border-slate-200 shadow-sm overflow-hidden" data-id="element-12434" data-mp="20qn3m2j8o69f">
        <div className="overflow-x-auto" data-id="element-12435" data-mp="25prgb35dgm4i">
          <table className="w-full border-collapse text-left" data-id="element-12436" data-mp="3w3zdu5rk6waf">
            <caption className="sr-only" data-id="element-12437" data-mp="17jz24emszo">
              Verhoudingstabel. Vul de ontbrekende getallen in.
            </caption>
            <thead data-id="element-12438" data-mp="90ksnskpfdjc">
              <tr className="bg-slate-50 border-b-2 border-slate-200" data-id="element-12439" data-mp="8z0028hya4o4">
                {headers.map((header, index) => <th key={index} id={`${tableId}-column-${index}`} scope="col" className="p-4 font-bold text-slate-700 min-w-[120px]" data-id="element-12440" data-mp="429ljplsnh4t" data-mpi={index}>
                  
                    <LatexRenderer content={String(header)} data-id="element-12441" data-mp="38f6avpciro6b" />
                  </th>)}
              </tr>
            </thead>
            <tbody data-id="element-12442" data-mp="3m6uoxy6bwmpx">
              {rows.map((row, rowIndex) => <tr key={rowIndex} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50 transition-colors" data-id="element-12443" data-mp="3m8nwdfornayz" data-mpi={rowIndex}>
                
                  {(row || []).map((cellContent, colIndex) => {
                const key = getKey(rowIndex, colIndex);
                const cell = editableCells.find(candidate => candidate.key === key);
                const header = headers[colIndex] || `kolom ${colIndex + 1}`;
                return <td key={colIndex} className="p-3" data-id="element-12444" data-mp="18zmpu943hbyn" data-mpi={colIndex}>
                        {isHidden(rowIndex, colIndex) && cell ? <input id={`${tableId}-${key}`} type="text" inputMode="decimal" pattern="[0-9.,\-]*" value={inputs[key] || ''} onChange={event => setInputs(previous => ({
                    ...previous,
                    [key]: event.target.value
                  }))} onKeyDown={event => handleInputKeyDown(event, cell)} disabled={shouldValidate} aria-label={`Ontbrekend getal in ${header}, rij ${rowIndex + 1}`} aria-describedby={`${tableId}-column-${colIndex}`} className="w-full min-w-[80px] min-h-[44px] p-2 text-center font-medium text-indigo-700 bg-indigo-50 border-2 border-indigo-200 rounded-lg focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all placeholder:text-indigo-200 disabled:cursor-default" placeholder="?" autoComplete="off" data-id="element-12445" data-mp="v8qavl33cta2" /> : <span className="text-slate-700 font-medium px-2" data-id="element-12446" data-mp="7gsy9rrthx7w">
                            <LatexRenderer content={cellToString(cellContent)} data-id="element-12447" data-mp="3jlnlz80kdmfx" />
                        
                          </span>}
                      </td>;
              })}
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-center text-slate-500 text-sm mt-4" data-id="element-12448" data-mp="35vacv2bxs1p0">
        Vul de ontbrekende getallen in. Gebruik Enter voor het volgende vakje of
        Alt met de pijltjestoetsen om door een rij of kolom te bewegen.
      </p>
    </div>;
}